export interface PostingSlot {
  id: string;          // unique id for React keys / removal
  dayOfWeek: number;   // 0=Sun, 1=Mon, ..., 6=Sat
  hour: number;        // 0-23
  minute: number;      // 0 or 30
}

export interface PostingScheduleConfig {
  slots: PostingSlot[];
  timezone: string;     // IANA timezone (e.g., "America/New_York")
}

export interface PlatformConnection {
  id: string;
  userId: string;
  platform: PlatformType;
  /** @deprecated OAuth credentials are server-only and are never returned. */
  accessToken?: string;
  /** @deprecated OAuth credentials are server-only and are never returned. */
  refreshToken?: string;
  expiresAt: number | null; // access-token expiry in epoch ms; null = no fixed expiry
  refreshExpiresAt?: number; // refresh-token expiry (ms); set for LinkedIn/TikTok
  profileId: string; // Platform-specific ID
  profileName: string; // Username/Channel name
  profileImage?: string; // Avatar URL
  entityType?: "person" | "organization";
  /** @deprecated Granted scopes are not part of the browser DTO. */
  scope?: string[];
  status: ConnectionStatus;
  // Why the connection is "expired", when known. "facebook_page_unselected":
  // the Page was unticked in Facebook's login dialog (often while connecting
  // another Page), which withdrew WaveGen's access to it.
  expiredReason?: "facebook_page_unselected" | null;
  createdAt: number;
  updatedAt: number;
  postingSchedule?: PostingScheduleConfig;
  // Publish settings remembered from this channel's last published or
  // scheduled post. Server-written by /schedule; seeds new selections.
  publishDefaults?: PublishDefaults;
  // Next due time for the daily account-level metrics snapshot (server-managed)
  accountMetricsNextPollAt?: number;
  // Latest account-level observation (server-written): follower count for UI
  audience?: {
    followerCount: number | null;
    capturedAt: number;
  };
  /** Safe capability summary; granted OAuth scopes remain server-only. */
  analyticsAccess?: {
    state: "ready" | "reconnect_required";
    reason:
      | "connection_expired"
      | "analytics_permission"
      | "follower_permission"
      | null;
    followerState: "ready" | "reconnect_required";
  };
}

export type PlatformType =
  | "instagram"
  | "youtube"
  | "tiktok"
  | "linkedin"
  | "facebook"
  | "pinterest";
export type ConnectionStatus = "active" | "expiring_soon" | "expired" | "revoked";

/** Default posting slots per platform (used when user has no custom schedule). */
export const DEFAULT_POSTING_SLOTS: Record<PlatformType, PostingSlot[]> = {
  instagram: [
    { id: "d1", dayOfWeek: 1, hour: 12, minute: 0 },
    { id: "d2", dayOfWeek: 1, hour: 18, minute: 0 },
    { id: "d3", dayOfWeek: 2, hour: 12, minute: 0 },
    { id: "d4", dayOfWeek: 2, hour: 18, minute: 0 },
    { id: "d5", dayOfWeek: 3, hour: 12, minute: 0 },
    { id: "d6", dayOfWeek: 3, hour: 18, minute: 0 },
    { id: "d7", dayOfWeek: 4, hour: 12, minute: 0 },
    { id: "d8", dayOfWeek: 4, hour: 18, minute: 0 },
    { id: "d9", dayOfWeek: 5, hour: 12, minute: 0 },
    { id: "d10", dayOfWeek: 5, hour: 18, minute: 0 },
    { id: "d11", dayOfWeek: 0, hour: 10, minute: 0 },
    { id: "d12", dayOfWeek: 6, hour: 10, minute: 0 },
  ],
  tiktok: [
    { id: "d1", dayOfWeek: 1, hour: 13, minute: 0 },
    { id: "d2", dayOfWeek: 1, hour: 20, minute: 0 },
    { id: "d3", dayOfWeek: 2, hour: 13, minute: 0 },
    { id: "d4", dayOfWeek: 2, hour: 20, minute: 0 },
    { id: "d5", dayOfWeek: 3, hour: 13, minute: 0 },
    { id: "d6", dayOfWeek: 3, hour: 20, minute: 0 },
    { id: "d7", dayOfWeek: 4, hour: 13, minute: 0 },
    { id: "d8", dayOfWeek: 4, hour: 20, minute: 0 },
    { id: "d9", dayOfWeek: 5, hour: 13, minute: 0 },
    { id: "d10", dayOfWeek: 5, hour: 20, minute: 0 },
    { id: "d11", dayOfWeek: 0, hour: 20, minute: 30 },
    { id: "d12", dayOfWeek: 6, hour: 20, minute: 30 },
  ],
  youtube: [
    { id: "d1", dayOfWeek: 1, hour: 13, minute: 30 },
    { id: "d2", dayOfWeek: 1, hour: 18, minute: 0 },
    { id: "d3", dayOfWeek: 2, hour: 13, minute: 30 },
    { id: "d4", dayOfWeek: 2, hour: 18, minute: 0 },
    { id: "d5", dayOfWeek: 3, hour: 13, minute: 30 },
    { id: "d6", dayOfWeek: 3, hour: 18, minute: 0 },
    { id: "d7", dayOfWeek: 4, hour: 13, minute: 30 },
    { id: "d8", dayOfWeek: 4, hour: 18, minute: 0 },
    { id: "d9", dayOfWeek: 5, hour: 13, minute: 30 },
    { id: "d10", dayOfWeek: 5, hour: 18, minute: 0 },
    { id: "d11", dayOfWeek: 0, hour: 10, minute: 0 },
    { id: "d12", dayOfWeek: 6, hour: 10, minute: 0 },
  ],
  linkedin: [
    { id: "d1", dayOfWeek: 1, hour: 8, minute: 0 },
    { id: "d2", dayOfWeek: 1, hour: 12, minute: 30 },
    { id: "d3", dayOfWeek: 2, hour: 8, minute: 0 },
    { id: "d4", dayOfWeek: 2, hour: 12, minute: 30 },
    { id: "d5", dayOfWeek: 3, hour: 8, minute: 0 },
    { id: "d6", dayOfWeek: 3, hour: 12, minute: 30 },
    { id: "d7", dayOfWeek: 4, hour: 8, minute: 0 },
    { id: "d8", dayOfWeek: 4, hour: 12, minute: 30 },
    { id: "d9", dayOfWeek: 5, hour: 8, minute: 0 },
    { id: "d10", dayOfWeek: 5, hour: 12, minute: 30 },
    { id: "d11", dayOfWeek: 0, hour: 11, minute: 0 },
    { id: "d12", dayOfWeek: 6, hour: 11, minute: 0 },
  ],
  facebook: [
    { id: "d1", dayOfWeek: 1, hour: 10, minute: 0 },
    { id: "d2", dayOfWeek: 1, hour: 14, minute: 0 },
    { id: "d3", dayOfWeek: 2, hour: 10, minute: 0 },
    { id: "d4", dayOfWeek: 2, hour: 14, minute: 0 },
    { id: "d5", dayOfWeek: 3, hour: 10, minute: 0 },
    { id: "d6", dayOfWeek: 3, hour: 14, minute: 0 },
    { id: "d7", dayOfWeek: 4, hour: 10, minute: 0 },
    { id: "d8", dayOfWeek: 4, hour: 14, minute: 0 },
    { id: "d9", dayOfWeek: 5, hour: 10, minute: 0 },
    { id: "d10", dayOfWeek: 5, hour: 14, minute: 0 },
    { id: "d11", dayOfWeek: 0, hour: 11, minute: 0 },
    { id: "d12", dayOfWeek: 6, hour: 11, minute: 0 },
  ],
  pinterest: [
    { id: "d1", dayOfWeek: 1, hour: 9, minute: 0 },
    { id: "d2", dayOfWeek: 1, hour: 20, minute: 0 },
    { id: "d3", dayOfWeek: 2, hour: 9, minute: 0 },
    { id: "d4", dayOfWeek: 2, hour: 20, minute: 0 },
    { id: "d5", dayOfWeek: 3, hour: 9, minute: 0 },
    { id: "d6", dayOfWeek: 3, hour: 20, minute: 0 },
    { id: "d7", dayOfWeek: 4, hour: 9, minute: 0 },
    { id: "d8", dayOfWeek: 4, hour: 20, minute: 0 },
    { id: "d9", dayOfWeek: 5, hour: 9, minute: 0 },
    { id: "d10", dayOfWeek: 5, hour: 20, minute: 0 },
    { id: "d11", dayOfWeek: 0, hour: 10, minute: 0 },
    { id: "d12", dayOfWeek: 6, hour: 10, minute: 0 },
  ],
};

export interface TikTokCreatorInfo {
  creator_avatar_url: string;
  creator_username: string;
  creator_nickname: string;
  privacy_level_options: string[];
  comment_disabled: boolean;
  duet_disabled: boolean;
  stitch_disabled: boolean;
  max_video_post_duration_sec: number;
}

/**
 * Per-channel publish settings carried from one post to the next. Titles,
 * captions and comment text are per post and never remembered; neither are
 * TikTok's per-post options (privacy, interactions, commercial content).
 */
export interface PublishDefaults {
  videoEnabled?: boolean;
  postType?: "feed" | "story";
  privacy?: "public" | "unlisted" | "private";
  madeForKids?: boolean;
  boardId?: string;
  boardName?: string;
  firstCommentEnabled?: boolean;
}
